import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import isEqual from "lodash/isEqual";
import Loader from "@/components/UI/Loader";
import { createPlotInfo } from "./functions/createPlotInfo";
import { createPlotClassName } from "./functions/utils/createPlotClassName";
import Legend from "./components/Legend";
import { VivoPlotWrapperProps } from "./types";
import { PlotControls } from "./components/PlotControls";
import { BoxSwarmToggle } from "./components/BoxSwarmToggle";
import { FilterButtonOverlay } from "./components/FilterButtonOverlay";
import { selectSwarmBoxSubject } from "./functions/utils/selectSwarmBoxSubject";
import { useViewportSize } from "@/utils/useTableSize";
import { useDebouncedValue } from "@/utils/useDebouncedValue";
import { EMPTY_TABLE_VIEW } from "./functions/plotTableView";
import type { PlotElementSelection } from "./functions/utils/plotSelection";

const useDeepStableValue = <T,>(value: T): T => {
  const ref = useRef(value);

  if (!isEqual(ref.current, value)) {
    ref.current = value;
  }

  return ref.current;
};

export const VivoPlot = ({
  data,
  type,
  x,
  y,
  subgroup,
  inModal,
  fill,
  config,
  dataIsLoading,
  selectedIdName,
  selectedIdValue = undefined,
  deletePlot,
  hidePlot,
  editPlot,
  duplicatePlot,
  onConfigChange,
  plotFilters,
  onPlotMetadataChange,
}: VivoPlotWrapperProps) => {
  const [isLoading, setIsLoading] = useState(dataIsLoading);

  const [isBoxplotInFront, setIsBoxplotInFront] = useState<boolean>(
    config.isBoxplotInFront ?? true,
  );
  const [showLegend, setShowLegend] = useState(config.showLegend || false);

  const [plotScales, setPlotScales] = useState([]);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const onPlotMetadataChangeRef = useRef(onPlotMetadataChange);
  const hasPublishedPlotMetadataRef = useRef(false);
  const filterClickHandlerRef = useRef(config.filterClickHandler);
  const onElementSelectRef = useRef(config.onElementSelect);
  const onPointActivateRef = useRef(config.onPointActivate);

  const RESIZE_DEBOUNCE_DELAY = 300;
  const viewportSize = useViewportSize();
  const debouncedViewportSize = useDebouncedValue(
    viewportSize,
    RESIZE_DEBOUNCE_DELAY,
  );
  const debouncedContainerSize = useDebouncedValue(
    containerSize,
    RESIZE_DEBOUNCE_DELAY,
  );

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    onPlotMetadataChangeRef.current = onPlotMetadataChange;
  }, [onPlotMetadataChange]);

  useEffect(() => {
    filterClickHandlerRef.current = config.filterClickHandler;
  }, [config.filterClickHandler]);

  const filterClickHandler = useCallback(
    () => filterClickHandlerRef.current?.(),
    [],
  );

  useEffect(() => {
    onElementSelectRef.current = config.onElementSelect;
  }, [config.onElementSelect]);

  // Held behind a ref so a new handler identity each render cannot force the
  // plot to redraw.
  const onElementSelect = useCallback(
    (selection: PlotElementSelection) =>
      onElementSelectRef.current?.(selection),
    [],
  );

  useEffect(() => {
    onPointActivateRef.current = config.onPointActivate;
  }, [config.onPointActivate]);

  const onPointActivate = useCallback(
    (entityKey: string, event: MouseEvent) =>
      onPointActivateRef.current?.(entityKey, event),
    [],
  );

  // Memoize scales
  const scales = useMemo(() => {
    const isHorizontal = config.orientation === "horizontal";
    const xPosition: "left" | "bottom" =
      isHorizontal && type === "barplot" ? "left" : "bottom";
    return [
      {
        ...x,
        position: xPosition,
      },
      { ...y, position: "left" as const },
      { ...subgroup, type: "subgroup" as const },
      { ...fill, type: "fill" as const },
    ].filter((scale) => scale.dataMapping && scale.dataMapping !== "none");
  }, [x, y, subgroup, fill, config.orientation, type]);
  const plotClassName = useMemo(
    () =>
      createPlotClassName(scales, { plotId: config.plotId, inModal } as any),
    [scales, config.plotId, inModal],
  );

  const plotId = config?.plotId;
  const ssjIdValue = config?.ssjIdValue;
  const pairFields = config.pairFields ?? config.pairKeys;
  const configWithoutFilterClickHandler = {
    ...config,
    filterClickHandler: undefined,
    onElementSelect: undefined,
    onPointActivate: undefined,
  };
  const plotConfigInput = {
    ...configWithoutFilterClickHandler,
    selectedIdName,
    selectedIdValue,
    showLegend,
    isBoxplotInFront,
    plotClassName,
    inModal,
    ...(pairFields ? { pairFields } : {}),
    filterClickHandler,
    ...(config.onElementSelect ? { onElementSelect } : {}),
    onPointActivate,
  };
  const stablePlotConfig = useDeepStableValue(plotConfigInput);
  const plotConfig = useMemo(
    () => ({ ...stablePlotConfig }),
    [stablePlotConfig],
  );
  const previewMetadataRequest = useDeepStableValue({
    data,
    dataIsLoading,
    type,
    xMapping: x?.dataMapping,
    yMapping: type === "barplot" ? undefined : y?.dataMapping,
    subgroupMapping: subgroup?.dataMapping,
    fillMapping: fill?.dataMapping,
    plotFilters,
    xSortDirection: config.xSortDirection,
    xSortBy: config.xSortBy,
    maxBarCount: config.maxBarCount,
    pairFields,
  });

  useEffect(() => {
    if (!hasPublishedPlotMetadataRef.current) return;

    hasPublishedPlotMetadataRef.current = false;
    onPlotMetadataChangeRef.current?.({ tableView: EMPTY_TABLE_VIEW });
  }, [previewMetadataRequest]);

  const editThisPlot = useCallback(() => {
    editPlot({
      data,
      type,
      x,
      y,
      subgroup,
      fill,
      config: {
        ...config,
        selectedIdName,
        selectedIdValue,
        showLegend,
        isBoxplotInFront,
      },
      dataIsLoading: false,
      plotFilters,
    });
  }, [
    data,
    type,
    x,
    y,
    subgroup,
    fill,
    config,
    selectedIdName,
    selectedIdValue,
    showLegend,
    isBoxplotInFront,
    editPlot,
    plotFilters,
  ]);

  const plotCreator = useMemo(
    () =>
      createPlotInfo({
        data,
        type,
        scales,
        config: plotConfig,
      }),
    [data, type, scales, plotConfig],
  );

  const pageSelector = config?.plotClassName;

  useEffect(() => {
    /* eslint-disable-next-line @eslint-react/set-state-in-effect -- TODO(eslint-baseline): address @eslint-react/set-state-in-effect */
    setShowLegend(config.showLegend || false);
  }, [config.showLegend]);

  useEffect(() => {
    /* eslint-disable-next-line @eslint-react/set-state-in-effect -- TODO(eslint-baseline): address @eslint-react/set-state-in-effect */
    setIsBoxplotInFront(config.isBoxplotInFront ?? true);
  }, [config.isBoxplotInFront]);

  const handleSetShowLegend = useCallback(
    (nextShowLegend: boolean) => {
      setShowLegend(nextShowLegend);
      if (!inModal && onConfigChange && plotId) {
        onConfigChange(plotId, { showLegend: nextShowLegend });
      }
    },
    [inModal, onConfigChange, plotId],
  );

  const handleSetIsBoxplotInFront = useCallback(
    (nextIsBoxplotInFront: boolean) => {
      setIsBoxplotInFront(nextIsBoxplotInFront);
      if (!inModal && onConfigChange && plotId) {
        onConfigChange(plotId, { isBoxplotInFront: nextIsBoxplotInFront });
      }
    },
    [inModal, onConfigChange, plotId],
  );

  useEffect(() => {
    plotCreator.changeTicksDisplay({
      showXTicks: config.showXTicks ?? true,
      showYTicks: config.showYTicks ?? true,
    });
  }, [config.showXTicks, config.showYTicks, plotCreator]);

  useEffect(() => {
    plotCreator.setSubject(selectedIdValue, showLegend);
  }, [selectedIdValue, showLegend, plotCreator]);

  useEffect(() => {
    plotCreator.changePlotOrder(isBoxplotInFront);
  }, [isBoxplotInFront, plotCreator]);

  useEffect(() => {
    let active = true;

    if (!dataIsLoading && plotCreator) {
      plotCreator
        .plotCreator({
          ssjIdValue,
          selectedIdValue,
          showLegend: showLegend,
        })
        .then((data) => {
          if (!active) return;

          setIsLoading(false);
          setPlotScales(data.plotScales);
          hasPublishedPlotMetadataRef.current = true;
          onPlotMetadataChangeRef.current?.({
            ...(data.pairingStats ? { pairingStats: data.pairingStats } : {}),
            tableView: data.tableView,
          });
          if (selectedIdValue && type === "swarm-box") {
            selectSwarmBoxSubject(plotClassName, selectedIdValue);
          }
        })
        /* eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO(eslint-baseline): address @typescript-eslint/no-unused-vars */
        .catch((error) => {
          if (!active) return;
          setIsLoading(false);
        });
    }

    return () => {
      active = false;
    };
  }, [
    dataIsLoading,
    data,
    type,
    x,
    y,
    subgroup,
    fill,
    debouncedViewportSize,
    debouncedContainerSize,
    showLegend,
    plotCreator,
    ssjIdValue,
    selectedIdValue,
    plotClassName,
  ]);

  // Track previous sizes to detect when they change
  const prevViewportSizeRef = useRef(debouncedViewportSize);
  const prevContainerSizeRef = useRef(debouncedContainerSize);

  useEffect(() => {
    if (!plotCreator) return;

    // Force update if viewport or container size changed
    const forceUpdate =
      prevViewportSizeRef.current.width !== debouncedViewportSize.width ||
      prevViewportSizeRef.current.height !== debouncedViewportSize.height ||
      prevContainerSizeRef.current.width !== debouncedContainerSize.width ||
      prevContainerSizeRef.current.height !== debouncedContainerSize.height;

    prevViewportSizeRef.current = debouncedViewportSize;
    prevContainerSizeRef.current = debouncedContainerSize;

    plotCreator.updatePlotLabel(plotCreator.config, forceUpdate);
  }, [
    plotCreator,
    plotCreator?.config.labels,
    debouncedViewportSize,
    debouncedContainerSize,
  ]);

  if (!x.dataMapping || (!y.dataMapping && type !== "barplot")) {
    return (
      <div
        className={`${inModal ? "h-[400px]" : "h-full"} relative flex w-full flex-col`}
      >
        <div className="w-full h-full flex items-center justify-center">
          Please define your plot configurations.
        </div>
      </div>
    );
  }
  return (
    <>
      <div
        ref={containerRef}
        className={`${inModal ? "h-[400px]" : "h-full"} relative flex w-full flex-col ${plotClassName} plot-container overflow-hidden rounded-lg`}
      >
        {(dataIsLoading || isLoading || !plotCreator) && (
          <div className="absolute top-0 left-0 w-full h-full bg-white flex items-center justify-center group">
            <Loader type="blue" noMargin />
          </div>
        )}

        <svg className={`${plotClassName} svg ${pageSelector} w-full h-full`} />
        <Legend
          fillScale={plotScales.find((scale) => scale.type === "fill")}
          showLegend={showLegend}
        />

        {config.hasPlotFilters && (
          <FilterButtonOverlay
            plotFilters={config.plotFilters}
            metadata={config.metadata}
            columnsTitleCreator={config.columnsTitleCreator}
            onClick={config.filterClickHandler}
            plotSize={config.plotSize}
          />
        )}

        <div className="absolute top-4 right-4">
          <div className="flex justify-between items-center  gap-2 z-99 h-[28px]">
            {type === "swarm-box" && config.showSwarm && (
              <BoxSwarmToggle
                isBoxplotInFront={isBoxplotInFront}
                setIsBoxplotInFront={handleSetIsBoxplotInFront}
              />
            )}
            <PlotControls
              inModal={inModal}
              showLegend={showLegend}
              setShowLegend={handleSetShowLegend}
              editThisPlot={editThisPlot}
              hidePlot={hidePlot}
              deletePlot={deletePlot}
              duplicatePlot={duplicatePlot}
              plotClassName={plotClassName}
              config={config}
              fillScale={plotScales.find((scale) => scale.type === "fill")}
            />
          </div>
        </div>
      </div>
    </>
  );
};
